import { toJS } from 'mobx';
import { useCallback } from 'react';
import { useStores } from '../../stores';
import {
  buildConditionsDetails,
  buildEmptyCohort,
  buildFilterFlowData,
} from '../helper-utils/cohortModalHelper';
import { ToggleValue } from './useReportsCohortItem';
import { ICohortDetails } from '../../_components/organisms/CreateNewModalCoreComponent';
import { roundNumber } from '../helper-utils/helper';

interface useCohortDetailsModalProps {
  handleClose: () => void;
  totalResults: number;
  onSave?: () => void;
  getTotalMembers: (
    _cohorts: ICohortDetails[],
    _toggleList: ToggleValue[],
  ) => Promise<number | null>;
  toggleList: ToggleValue[];
}

export const useCohortDetailsModal = ({
  handleClose,
  totalResults,
  onSave,
  getTotalMembers,
  toggleList,
}: useCohortDetailsModalProps) => {
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const cohortDetailsList = toJS(cohortDetailsStore.draftCohortDetailsList);

  const isSaveButtonDisabled =
    cohortDetailsStore.draftMemberUniqueIdList?.length === 0;
  const cohortSelectionsTypes = toJS(cohortDetailsStore.cohortSelectionsTypes);

  const handleAddCohort = useCallback(() => {
    cohortDetailsStore.setDraftCohortDetailsList([
      ...cohortDetailsList,
      buildEmptyCohort(cohortDetailsList.length + 1),
    ]);
  }, [cohortDetailsList, cohortDetailsStore]);

  const handleDeleteCohort = useCallback(
    async (itemIndex: number, refresh = false) => {
      const updatedList = refresh
        ? cohortDetailsList.map((cohort, index) =>
            index === itemIndex
              ? {
                  ...cohort,
                  category: '',
                  condition: '',
                  property: '',
                  subCategory: '',
                  results: '',
                  multipleProperties: [],
                }
              : cohort,
          )
        : cohortDetailsList.filter((_, index) => index !== itemIndex);

      const updatedToggleList = refresh
        ? toggleList
        : itemIndex === 0
        ? toggleList.slice(1)
        : toggleList.filter((_, i) => i !== itemIndex - 1);

      cohortDetailsStore.setDraftCohortDetailsList(updatedList);
      cohortDetailsStore.setToggleList(updatedToggleList);

      const hasCompleteRow = updatedList.some(
        (c) =>
          c.category &&
          c.condition &&
          (c.property ||
            (Array.isArray(c.multipleProperties) &&
              c.multipleProperties.length > 0)),
      );

      if (hasCompleteRow) {
        const results = await getTotalMembers(updatedList, updatedToggleList);
        const count = results ?? 0;
        const pct = roundNumber((count / totalResults) * 100, 2);
        updatedList[updatedList.length - 1].results = String(count);
        updatedList[updatedList.length - 1].percentage = String(pct);

        const totalCount = totalResults;
        const updatedFlowData = buildFilterFlowData(
          updatedList,
          cohortSelectionsTypes,
          totalCount,
        );
        cohortDetailsStore.setDraftFilterFlowData(updatedFlowData);
      } else {
        cohortDetailsStore.setDraftFilterFlowData([
          {
            label: 'Total No. of Patients',
            count: totalResults,
            percent: '100%',
            type: 'TOTAL',
          },
        ]);
      }
    },
    [
      cohortDetailsList,
      cohortDetailsStore,
      totalResults,
      getTotalMembers,
      toggleList,
    ],
  );
  const handleClearFilter = useCallback(() => {
    cohortDetailsStore.resetDraft(totalResults);
  }, [cohortDetailsStore, totalResults]);

  const handleSave = useCallback(async () => {
    cohortDetailsStore.commitDraft();
    const conditionsDetails = buildConditionsDetails(
      toJS(cohortDetailsStore.cohortDetailsList),
    );
    cohortDetailsStore.setConditionsDetails(conditionsDetails);
    onSave?.();
    handleClose();
  }, [cohortDetailsStore, handleClose, onSave]);

  const handleCancel = useCallback(() => {
    cohortDetailsStore.discardDraft();
    handleClose();
  }, [cohortDetailsStore, handleClose]);

  return {
    isSaveButtonDisabled,
    handleAddCohort,
    handleDeleteCohort,
    handleClearFilter,
    handleSave,
    handleCancel,
  };
};
